// PostHog session replay — replay-only, and it must stay that way.
//
// PostHog is a suite (product analytics, feature flags, surveys, replay) and
// each product is metered separately against an allocation shared across the
// whole ORGANISATION — every MGS product under the account draws on the same
// budget. So a single project that quietly captures pageviews spends everyone
// else's allowance. Every `false` below is load-bearing. Do not add
// `posthog.capture(...)` calls here; product events belong in the self-hosted
// analytics stack, not in PostHog. See ops/posthog.md.
//
// We import the `module.full.no-external` build rather than the default entry
// on purpose. The default `posthog-js` entry ships a stub that fetches its
// session recorder at runtime from `us-assets.i.posthog.com` — under our
// enforcing CSP that would mean adding a third-party origin to `script-src`,
// i.e. allowing remote executable code, for temporary instrumentation. This
// build has the recorder compiled in and fetches no scripts at all, so the
// only CSP concession is a `connect-src` entry for the ingestion endpoint.
import posthog from "posthog-js/dist/module.full.no-external"

// Rendered by shared/_session_replay.html.erb, and only into the `application`
// and `marketing` layouts. Absent on /admin and /embed, and absent everywhere
// when POSTHOG_KEY is unset — in every one of those cases this is a no-op.
const key = document.querySelector('meta[name="posthog-key"]')?.content
const apiHost = document.querySelector('meta[name="posthog-host"]')?.content

// Set by the server for staff/admin users (SessionReplay#internal?). A boolean —
// the email never reaches the page.
const optOut = document.querySelector('meta[name="posthog-opt-out"]')?.content === "true"

if (key) {
  posthog.init(key, {
    api_host: apiHost || "https://us.i.posthog.com",

    // --- replay-only: each of these is a separately-metered product ---
    autocapture: false, // no click/DOM events
    capture_pageview: false, // no pageviews
    // NOT redundant with capture_pageview — `capture_pageleave` defaults to ON
    // and is its own product-analytics event. Turning off pageviews does not
    // turn off pageleaves; we shipped a "replay only" product elsewhere that
    // streamed $pageleave into the shared meter for weeks on this exact bug.
    capture_pageleave: false,
    disable_surveys: true, // no surveys

    session_recording: {
      // Hides typed values. We still see clicks, scrolls, and where people
      // stall, which is what we're actually after on the hero form and the
      // email gate. Unmasking would put real emails and destination URLs into
      // the recordings — a deliberate PII-recording choice with a deletion
      // obligation, not a default. Leave this true.
      maskAllInputs: true,
    },

    // Persistence is deliberately left at its default (cookie + localStorage).
    // Do NOT set `persistence: "memory"`: it looks like the privacy-friendly
    // cookieless choice, but the recorder needs a *persisted* session id to
    // stitch its snapshots into a watchable replay. With memory persistence the
    // events arrive and no viewable session ever appears.
  })

  // Exclude our own sessions. Called synchronously right after init, so it
  // lands long before the recorder could start — recording only begins once
  // the remote config comes back over the network.
  //
  // Why opt out on the client instead of just withholding the key server-side:
  // `opt_out_capturing()` writes a PERSISTED flag (localStorage
  // `__ph_opt_in_out_<token>`) that the SDK re-reads on every later init. So one
  // signed-in page view silences this browser **for good — including after we
  // sign out.** That's the case that matters: the surface we most want to watch
  // is the anonymous landing/builder funnel, which is exactly what we're on when
  // testing signed-out. A server-side gate would go quiet while we're signed in
  // and then cheerfully record us the moment we logged out.
  //
  // Per browser/profile, and cleared by wiping site data. To undo, see the
  // opt-in snippet in ops/posthog.md.
  if (optOut) {
    posthog.opt_out_capturing()
  }
}

export default posthog
